import { createTheme } from '@mui/material/styles';
import tokens from './tokens.json';

// Brand + semantic values from tokens.json (single SoT).
const COLORS = {
  // Figma brand
  onyxBlack: tokens.brand.black,
  onyxWhite: tokens.brand.white,
  onyxPurple: tokens.brand.purple,
  onyxPurpleDark: tokens.brand.purpleDark,
  onyxPurpleLight: tokens.brand.purpleLight,
  onyxPeach: tokens.brand.peach,
  onyxGreenGold: tokens.brand.greenGold,
  onyxLightBlue: tokens.brand.lightBlue,
  onyxGrey: tokens.brand.grey,
  onyxGreyDark: tokens.brand.greyDark,
  onyxBg: tokens.semantic.light.bg,
  onyxSurface: tokens.semantic.light.surface,
  onyxText: tokens.semantic.light.text,
  onyxTextMuted: tokens.semantic.light.textMuted,
  onyxTextSubtle: tokens.semantic.light.textSubtle,
  onyxBorder: tokens.semantic.light.border,
  onyxBorderStrong: tokens.semantic.light.borderStrong,
  onyxLavender: tokens.brand.purple,
  onyxAccent: tokens.semantic.light.accent,
  onyxCoral: tokens.brand.peach,
  onyxOrange: tokens.brand.peach,
  onyxMint: '#a8dcc8',
  onyxMustard: tokens.brand.greenGold,
  onyxLilac: '#d8ccef',
  onyxSeafoam: '#a8dcc8',

  // Canonical aliases
  black: tokens.brand.black,
  white: tokens.brand.white,
  // Legacy aliases → Figma (temporary during migration)
  indigo: '#884bda',
  indigoLight: '#cfaefa',
  indigoDark: '#6e3bb8',
  emerald: '#1bc99f',
  emeraldLight: '#4dd4b5',
  emeraldDark: '#15a082',
  orange: '#ff7758',
  orangeLight: '#ff9480',
  orangeDark: '#e65f42',
  yellow: '#cdc935',
  yellowLight: '#d9d65c',
  yellowDark: '#b8b42e',
  blue: '#3885ff',
  blueLight: '#639eff',
  blueDark: '#326fe6',
  grey50: '#f8f9fa',
  grey100: '#F7F7F7',
  grey200: '#f1eff5',
  grey300: '#dee2e6',
  grey400: '#c3c8c8',
  grey500: '#adb5bd',
  grey600: '#464949',
  grey700: '#495057',
  grey800: '#2a2e35',
  border: '#d9dee4',
  borderLight: '#c3c8c8',
  textHeading: '#000000',
  // Success status — Onyx olive / green-gold (not Material #2e7d32)
  successBg: tokens.status.success.bg,
  success: tokens.status.success.fg,
  successBorder: tokens.status.success.border,
  warningBg: tokens.status.warning.bg,
  warning: tokens.status.warning.fg,
  warningBorder: tokens.status.warning.border,
  errorBg: tokens.status.error.bg,
  error: tokens.status.error.fg,
  errorBorder: tokens.status.error.border,
  // Info stays neutral (light-blue / grey) — not purple status chips
  infoBg: tokens.brand.lightBlue,
  info: tokens.brand.greyDark,
  infoBorder: tokens.brand.grey,
  purple: '#884bda',
  purpleBg: '#f1eff5',
  purpleBorder: '#cfaefa',
};

export { COLORS };

const FONT_DISPLAY = "'Agate Recut', Georgia, 'Times New Roman', serif";
const FONT_UI = "'Founders Grotesk', system-ui, sans-serif";
const FONT_LABEL = "'Fragment Mono', ui-monospace, monospace";
const FONT_FIGURE = "'Fragment Mono', 'Fira Code', ui-monospace, monospace";

const muiTheme = createTheme({
  typography: {
    fontFamily: FONT_UI,
    fontSize: 16,
    h1: {
      fontFamily: FONT_DISPLAY,
      fontSize: '8rem',
      fontWeight: 400,
      lineHeight: 0.9,
      color: 'var(--color-text)',
      marginTop: '3.2rem',
      marginBottom: '1.6rem',
    },
    h2: {
      fontFamily: FONT_DISPLAY,
      fontSize: '5.6rem',
      fontWeight: 400,
      lineHeight: 1,
      color: 'var(--color-text)',
      marginTop: '3.2rem',
      marginBottom: '1.6rem',
    },
    h3: {
      fontFamily: FONT_UI,
      fontSize: '3.2rem',
      fontWeight: 400,
      lineHeight: 1.25,
      color: 'var(--color-text)',
      marginTop: '3.2rem',
      marginBottom: '1.6rem',
    },
    h4: {
      fontFamily: FONT_UI,
      fontSize: '2.6rem',
      fontWeight: 400,
      color: COLORS.onyxText,
      marginTop: '1.6rem',
      marginBottom: '1.6rem',
    },
    h5: { fontFamily: FONT_UI, fontSize: '2rem', fontWeight: 500, color: COLORS.onyxText, marginTop: '1.6rem', marginBottom: '1.6rem' },
    h6: { fontFamily: FONT_UI, fontSize: '1.6rem', fontWeight: 500, color: COLORS.onyxText, marginTop: '1.6rem', marginBottom: '1.6rem' },
    sectionTitle: { fontFamily: FONT_UI, fontSize: '1.8rem', fontWeight: 600, color: COLORS.onyxText },
    subtitle1: { fontFamily: FONT_UI, fontSize: '1.6rem', fontWeight: 600 },
    body1: { fontFamily: FONT_UI, fontSize: '1.6rem', lineHeight: 1.5 },
    body2: { fontFamily: FONT_UI, fontSize: '1.4rem', lineHeight: 1.4, color: COLORS.onyxTextMuted },
    button: { fontFamily: FONT_UI, fontSize: '1.6rem', fontWeight: 500, textTransform: 'none' },
    caption: { fontFamily: FONT_UI, fontSize: '1.3rem' },
    overline: { fontFamily: FONT_LABEL, fontSize: '1.2rem', letterSpacing: '0.08em' },
    brandDisplay: {
      fontFamily: FONT_DISPLAY,
      fontSize: '2.6rem',
      fontWeight: 500,
      // Follow semantic text token (remaps under [data-theme="dark"]).
      color: 'var(--color-text)',
      letterSpacing: '-0.01em',
      lineHeight: 1.15,
    },
    uiBody: {
      fontFamily: FONT_UI,
      fontSize: '1.4rem',
      fontWeight: 400,
      color: COLORS.onyxText,
      lineHeight: 1.45,
    },
    label: {
      fontFamily: FONT_LABEL,
      fontSize: '1.2rem',
      fontWeight: 400,
      color: COLORS.onyxTextMuted,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },
    bigFigure: {
      fontFamily: FONT_FIGURE,
      fontSize: '11.2rem',
      fontWeight: 400,
      lineHeight: 1,
      color: COLORS.onyxText,
    },
  },

  palette: {
    primary: { main: COLORS.onyxBlack, light: COLORS.grey700, dark: COLORS.grey800, contrastText: COLORS.onyxWhite },
    secondary: { main: COLORS.onyxBlack, light: COLORS.grey700, dark: COLORS.grey800, contrastText: COLORS.onyxWhite },
    error: { main: COLORS.orange, light: COLORS.orangeLight, dark: COLORS.orangeDark, contrastText: COLORS.white },
    warning: { main: COLORS.yellow, light: COLORS.yellowLight, dark: COLORS.yellowDark, contrastText: COLORS.black },
    info: { main: COLORS.info, light: COLORS.infoBg, dark: COLORS.onyxGreyDark, contrastText: COLORS.onyxWhite },
    success: {
      main: COLORS.onyxGreenGold,
      light: COLORS.successBg,
      dark: COLORS.success,
      contrastText: COLORS.onyxBlack,
    },
    purple: { main: COLORS.purple, light: COLORS.purpleBg, dark: COLORS.indigoDark, contrastText: COLORS.white },
    text: { primary: COLORS.onyxText, secondary: COLORS.onyxTextMuted, disabled: 'rgba(0, 0, 0, 0.38)' },
    background: { default: COLORS.onyxSurface, paper: COLORS.onyxSurface },
    divider: COLORS.onyxBorder,
    grey: {
      50: COLORS.grey50, 100: COLORS.grey100, 200: COLORS.grey200, 300: COLORS.grey300,
      400: COLORS.grey400, 500: COLORS.grey500, 600: COLORS.grey600, 700: COLORS.grey700,
      800: COLORS.grey800, 900: COLORS.black,
    },
  },

  spacing: 8,
  shape: { borderRadius: 0 },

  breakpoints: {
    values: {
      xs: 0,
      sm: 480,
      md: 768,
      lg: 1024,
      xl: 1440,
    },
  },

  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1100,
    drawer: 5000,
    modal: 10000,
    snackbar: 15000,
    tooltip: 15000,
  },

  // Flat elevation — overlays use border + scrim (DESIGN_SYSTEM decisions)
  shadows: Array(25).fill('none'),

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          boxSizing: 'border-box',
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        // Product default — Lighthouse / member chrome density.
        // Marketing Figma Primary (py 15 / px 25 / min 240) is size="large".
        root: {
          fontFamily: FONT_UI,
          fontSize: '1.6rem',
          fontWeight: 400,
          lineHeight: 1.25,
          letterSpacing: '0.03em',
          padding: '0.8rem 1.6rem',
          minWidth: 'unset',
          borderRadius: 0,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'background 0.2s ease, color 0.2s ease, border-color 0.2s ease',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          backgroundColor: COLORS.onyxBlack,
          color: COLORS.onyxLightBlue,
          '&:hover': { backgroundColor: COLORS.grey800, color: COLORS.onyxLightBlue },
        },
        outlined: {
          borderColor: 'var(--color-text)',
          borderWidth: '1px',
          color: 'var(--color-text)',
          backgroundColor: 'var(--color-surface)',
          borderRadius: 0,
          '&:hover': { backgroundColor: 'var(--color-bg)', borderColor: 'var(--color-text)' },
        },
        sizeSmall: {
          fontSize: '1.4rem',
          padding: '0.6rem 1.2rem',
          lineHeight: 1.25,
          minWidth: 'unset',
        },
        sizeLarge: {
          fontSize: '1.6rem',
          padding: '1.5rem 2.5rem',
          minWidth: '24rem',
        },
      },
      variants: [
        {
          props: { 'data-size': 'xsmall' },
          style: {
            fontSize: '1.2rem',
            padding: '0.4rem 0.8rem',
            minWidth: 'unset',
            lineHeight: 1.2,
            borderRadius: 0,
          },
        },
        {
          props: { variant: 'onyxPrimary' },
          style: {
            fontFamily: FONT_UI,
            fontWeight: 400,
            letterSpacing: '0.03em',
            textTransform: 'none',
            backgroundColor: COLORS.onyxBlack,
            color: COLORS.onyxLightBlue,
            borderRadius: 0,
            boxShadow: 'none',
            fontSize: '1.6rem',
            lineHeight: 1.25,
            '&:hover': { backgroundColor: COLORS.grey800, boxShadow: 'none', color: COLORS.onyxLightBlue },
            '&.Mui-disabled': {
              backgroundColor: COLORS.grey300,
              color: COLORS.grey600,
            },
          },
        },
        {
          props: { variant: 'onyxSecondary' },
          style: {
            fontFamily: FONT_UI,
            fontWeight: 400,
            letterSpacing: '0.03em',
            textTransform: 'none',
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text)',
            border: '1px solid var(--color-border-strong)',
            borderRadius: 0,
            boxShadow: 'none',
            fontSize: '1.6rem',
            lineHeight: 1.25,
            '&:hover': { backgroundColor: 'var(--color-bg)', boxShadow: 'none' },
          },
        },
      ],
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          boxShadow: 'none',
          border: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: FONT_DISPLAY,
          fontSize: '2rem',
          fontWeight: 400,
          lineHeight: 1.3,
          padding: '2rem 2rem 1rem 2rem',
          borderBottom: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '2rem',
          fontSize: '1.6rem',
          lineHeight: 1.5,
          '&.MuiDialogContent-dividers': {
            borderTop: `1px solid ${COLORS.border}`,
            borderBottom: `1px solid ${COLORS.border}`,
          },
        },
      },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '1rem 2rem 2rem 2rem',
          // Token-driven so [data-theme="dark"] remaps footer (not hardcoded grey50).
          borderTop: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-surface)',
          gap: '1rem',
          '& .MuiButton-root': {
            padding: '0.8rem 1.6rem',
            minWidth: '120px',
            borderRadius: 0,
            fontWeight: 500,
          },
          '& .MuiButton-text': {
            color: 'var(--color-text)',
            '&:hover': {
              backgroundColor: 'var(--color-bg)',
            },
          },
          '& .MuiButton-contained': {
            backgroundColor: 'var(--onyx-black)',
            color: 'var(--onyx-light-blue)',
            boxShadow: 'none',
            borderRadius: 0,
            '&:hover': {
              backgroundColor: 'var(--grey-800, #2a2e35)',
              boxShadow: 'none',
              color: 'var(--onyx-light-blue)',
            },
            '&.Mui-disabled': {
              backgroundColor: 'var(--grey-300)',
              color: 'var(--color-text-muted)',
              boxShadow: 'none',
            },
          },
          '& .MuiButton-outlined': {
            borderColor: 'var(--onyx-black)',
            color: 'var(--onyx-black)',
            backgroundColor: 'var(--color-surface)',
            borderRadius: 0,
            '&:hover': {
              backgroundColor: 'var(--color-bg)',
              borderColor: 'var(--onyx-black)',
            },
          },
        },
      },
    },

    MuiChip: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          fontSize: '1.3rem',
          fontWeight: 400,
          borderRadius: 0,
          maxWidth: 'none',
        },
        label: {
          overflow: 'visible',
          textOverflow: 'clip',
          whiteSpace: 'nowrap',
          paddingLeft: '0.8rem',
          paddingRight: '0.8rem',
        },
        sizeSmall: {
          fontSize: '1.2rem',
          height: 'auto',
          minHeight: '2.4rem',
          '& .MuiChip-label': {
            paddingTop: '0.2rem',
            paddingBottom: '0.2rem',
          },
        },
        colorSuccess: {
          backgroundColor: COLORS.successBg,
          color: COLORS.success,
          border: `1px solid ${COLORS.successBorder}`,
        },
        colorWarning: {
          backgroundColor: COLORS.warningBg,
          color: COLORS.warning,
          border: `1px solid ${COLORS.warningBorder}`,
        },
        colorError: {
          backgroundColor: COLORS.errorBg,
          color: COLORS.error,
          border: `1px solid ${COLORS.errorBorder}`,
        },
        colorInfo: {
          backgroundColor: COLORS.infoBg,
          color: COLORS.info,
          border: `1px solid ${COLORS.infoBorder}`,
        },
      },
      variants: [
        {
          props: { color: 'purple' },
          style: {
            backgroundColor: COLORS.onyxPurpleLight || COLORS.purpleBg,
            color: COLORS.onyxBlack,
            border: `1px solid ${COLORS.onyxBorder || COLORS.borderLight}`,
            fontFamily: FONT_LABEL,
            fontWeight: 400,
            letterSpacing: '0.06em',
          },
        },
        {
          props: { color: 'default' },
          style: {
            backgroundColor: COLORS.grey50,
            color: COLORS.grey600,
            border: `1px solid ${COLORS.borderLight}`,
          },
        },
        {
          props: { color: 'fu' },
          style: {
            backgroundColor: COLORS.onyxMustard,
            color: COLORS.onyxBlack,
            border: 'none',
            fontFamily: FONT_LABEL,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          },
        },
        {
          props: { color: 'responded' },
          style: {
            backgroundColor: COLORS.onyxCoral,
            color: COLORS.onyxWhite,
            border: 'none',
            fontFamily: FONT_LABEL,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          },
        },
        {
          props: { color: 'ltfu' },
          style: {
            backgroundColor: COLORS.onyxLilac,
            color: COLORS.onyxBlack,
            border: 'none',
            fontFamily: FONT_LABEL,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          },
        },
        {
          props: { color: 'mrrEligible' },
          style: {
            backgroundColor: COLORS.onyxMint,
            color: COLORS.onyxBlack,
            border: 'none',
          },
        },
        {
          props: { color: 'mrrIneligible' },
          style: {
            backgroundColor: COLORS.onyxCoral,
            color: COLORS.onyxWhite,
            border: 'none',
          },
        },
        {
          props: { color: 'mrrReview' },
          style: {
            backgroundColor: COLORS.onyxMustard,
            color: COLORS.onyxBlack,
            border: 'none',
          },
        },
      ],
    },

    MuiTextField: {
      styleOverrides: {
        root: {},
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: FONT_LABEL,
          fontSize: '1.6rem',
          fontWeight: 400,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          '&.Mui-focused': { color: COLORS.onyxBlack },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '1.6rem',
          borderRadius: 0,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-border)' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--color-border-strong)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-border-strong)',
            borderWidth: '1px',
          },
        },
        input: { padding: '1.2rem 1.4rem' },
        multiline: { padding: '0.8rem 1.4rem' },
        inputMultiline: { padding: 0, lineHeight: '1.5', minHeight: 'unset !important' },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: { fontSize: '1.6rem' },
        input: {},
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: { fontSize: '1.6rem', padding: '1.2rem 1.4rem' },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '1.4rem',
          padding: '0.5rem 1rem',
          minHeight: 'unset',
          borderRadius: 0,
          margin: '0.1rem 0.4rem',
          transition: 'all 0.15s ease-in-out',
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
          '&.Mui-selected': { backgroundColor: 'rgba(0, 0, 0, 0.06)' },
          '&.Mui-selected:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' },
        },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '1.3rem',
          '&.Mui-error': { color: COLORS.orange },
        },
      },
    },

    MuiFormControl: {
      styleOverrides: {
        root: { marginBottom: '2rem' },
      },
    },

    MuiTable: {
      styleOverrides: {
        root: {
          fontFamily: FONT_UI,
          backgroundColor: 'var(--color-surface)',
        },
      },
      variants: [
        {
          props: { 'data-variant': 'detail' },
          style: {
            '& .MuiTableCell-root': {
              padding: '0.6rem 1.2rem',
              fontSize: '1.4rem',
              borderBottom: '1px solid var(--color-border)',
            },
            '& .MuiTableCell-root[component="th"], & th.MuiTableCell-root': {
              fontFamily: FONT_LABEL,
              fontWeight: 400,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--color-text-subtle)',
              width: '35%',
              backgroundColor: 'var(--color-surface)',
            },
            '& .MuiTableRow-root:nth-of-type(even)': {
              backgroundColor: 'transparent',
            },
            '& .MuiTableRow-root:hover': {
              backgroundColor: 'transparent',
            },
          },
        },
        {
          // Alias — default table chrome is already Lighthouse.
          props: { 'data-variant': 'lighthouse' },
          style: {},
        },
      ],
    },

    MuiTableCell: {
      styleOverrides: {
        head: {
          fontFamily: FONT_LABEL,
          fontSize: '1.05rem',
          fontWeight: 400,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--color-text-subtle)',
          backgroundColor: 'var(--color-surface)',
          borderBottom: '1px solid var(--color-border)',
          padding: '1.2rem 1.6rem 1rem',
          position: 'sticky',
          top: 0,
          zIndex: 2,
        },
        body: {
          fontFamily: FONT_UI,
          fontSize: '1.45rem',
          fontWeight: 400,
          color: 'var(--color-text)',
          borderBottom: 'none',
          padding: '1.4rem 1.6rem',
          verticalAlign: 'middle',
          '& .MuiFormControl-root': { marginBottom: 0 },
        },
        sizeSmall: {
          padding: '1.2rem 1.6rem',
          fontSize: '1.45rem',
          lineHeight: 1.35,
          '&.MuiTableCell-head': {
            padding: '1.2rem 1.6rem 1rem',
            fontSize: '1.05rem',
          },
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--color-surface) !important',
          '&:nth-of-type(even)': { backgroundColor: 'var(--color-surface) !important' },
          '&:hover': { backgroundColor: 'var(--color-bg) !important' },
        },
      },
    },

    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: 'none',
          marginBottom: '1rem',
          overflowX: 'auto',
          boxShadow: 'none',
          backgroundColor: 'var(--color-surface)',
        },
      },
    },

    MuiTypography: {
      styleOverrides: {
        root: {},
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundImage: 'none',
        },
        rounded: { borderRadius: 0 },
        elevation1: { boxShadow: 'none', border: `1px solid ${COLORS.onyxBorder}` },
        elevation2: { boxShadow: 'none', border: `1px solid ${COLORS.onyxBorder}` },
        elevation3: { boxShadow: 'none', border: `1px solid ${COLORS.onyxBorder}` },
        elevation4: { boxShadow: 'none', border: `1px solid ${COLORS.onyxBorder}` },
      },
      variants: [
        {
          props: { 'data-variant': 'info' },
          style: {
            padding: '1.6rem',
            borderRadius: 0,
            fontSize: '1.4rem',
            lineHeight: 1.5,
          },
        },
        {
          props: { 'data-variant': 'info', color: 'info' },
          style: {
            backgroundColor: COLORS.infoBg,
            border: `1px solid ${COLORS.infoBorder}`,
            color: COLORS.info,
          },
        },
        {
          props: { 'data-variant': 'info', color: 'success' },
          style: {
            backgroundColor: COLORS.successBg,
            border: `1px solid ${COLORS.successBorder}`,
            color: COLORS.success,
          },
        },
        {
          props: { 'data-variant': 'info', color: 'warning' },
          style: {
            backgroundColor: COLORS.warningBg,
            border: `1px solid ${COLORS.warningBorder}`,
            color: COLORS.warning,
          },
        },
        {
          props: { 'data-variant': 'info', color: 'error' },
          style: {
            backgroundColor: COLORS.errorBg,
            border: `1px solid ${COLORS.errorBorder}`,
            color: COLORS.error,
          },
        },
      ],
    },

    MuiIconButton: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          color: COLORS.grey600,
          '&:hover': { backgroundColor: COLORS.grey100, color: COLORS.grey700 },
        },
      },
    },

    MuiFab: {
      defaultProps: {
        size: 'large',
      },
      styleOverrides: {
        root: {
          fontFamily: FONT_UI,
          width: '56px',
          height: '56px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          transition: 'background 0.3s ease-out, box-shadow 0.3s ease-out, transform 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2)',
          },
        },
        primary: {
          backgroundColor: COLORS.black,
          color: COLORS.white,
          '&:hover': { backgroundColor: COLORS.grey800 },
        },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: {
          fontFamily: FONT_UI,
          fontWeight: 600,
          fontSize: '1.1rem',
          minWidth: '20px',
          height: '20px',
        },
        colorSecondary: {
          backgroundColor: COLORS.emerald,
          color: COLORS.white,
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: COLORS.border },
      },
    },

    MuiStack: {
      defaultProps: { spacing: 3 },
    },

    MuiBox: {
      styleOverrides: {
        root: {},
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: COLORS.grey400,
          '&.Mui-checked': { color: COLORS.onyxBlack },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          color: COLORS.grey400,
          '&.Mui-checked': { color: COLORS.onyxBlack },
        },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': { color: COLORS.onyxBlack },
          '&.Mui-checked + .MuiSwitch-track': { backgroundColor: COLORS.onyxBlack },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: COLORS.onyxBlack,
          height: 2,
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: FONT_LABEL,
          fontSize: '1.2rem',
          fontWeight: 400,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: COLORS.onyxTextMuted,
          minHeight: '4rem',
          borderRadius: 0,
          '&.Mui-selected': { color: COLORS.onyxBlack },
        },
      },
    },

    MuiCircularProgress: {
      styleOverrides: {
        root: { color: COLORS.onyxBlack },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '1.3rem',
          backgroundColor: COLORS.grey800,
          padding: '0.8rem 1.2rem',
          borderRadius: 0,
        },
      },
    },

    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          boxShadow: 'none',
          border: `1px solid ${COLORS.onyxBorder}`,
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          boxShadow: 'none',
          border: `1px solid ${COLORS.onyxBorder}`,
          padding: '0.5rem 0',
          marginTop: '0.5rem',
        },
        list: { padding: '0.4rem 0' },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: '1.4rem',
          borderRadius: 0,
          padding: '1.2rem 1.6rem',
          border: '1px solid',
          fontWeight: 400,
          '& .MuiAlert-message': {
            fontFamily: FONT_UI,
            fontSize: '1.4rem',
            fontWeight: 400,
            color: 'inherit',
            padding: 0,
          },
          '& .MuiAlert-icon': { color: 'inherit', padding: 0, marginRight: '1.2rem' },
          '& .MuiAlert-message strong, & .MuiAlert-message b': {
            fontWeight: 500,
            color: 'inherit',
          },
        },
        standardInfo: {
          backgroundColor: COLORS.infoBg,
          color: COLORS.info,
          borderColor: COLORS.infoBorder,
        },
        standardWarning: {
          backgroundColor: COLORS.warningBg,
          color: COLORS.warning,
          borderColor: COLORS.warningBorder,
        },
        standardSuccess: {
          backgroundColor: COLORS.successBg,
          color: COLORS.success,
          borderColor: COLORS.successBorder,
        },
        standardError: {
          backgroundColor: COLORS.errorBg,
          color: COLORS.error,
          borderColor: COLORS.errorBorder,
        },
      },
    },

    // --- New component overrides (previously only in CSS files) ---

    MuiAutocomplete: {
      styleOverrides: {
        listbox: { backgroundColor: COLORS.white },
        option: {
          fontSize: '1.4rem',
          color: COLORS.black,
          padding: '0.8rem 1.2rem',
          '&:hover': { backgroundColor: COLORS.grey100 },
        },
        noOptions: {
          fontSize: '1.4rem',
          color: COLORS.grey600,
          padding: '0.8rem 1.2rem',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: '1px solid var(--color-border)',
          boxShadow: 'none',
          backgroundColor: 'var(--color-surface)',
        },
      },
      variants: [
        {
          props: { 'data-variant': 'stat' },
          style: {
            padding: '1.6rem',
            textAlign: 'center',
            '& .MuiCardContent-root': {
              padding: '0',
            },
          },
        },
      ],
    },

    MuiCardContent: {
      styleOverrides: {
        root: { padding: '2rem' },
      },
    },

    MuiStepper: {
      styleOverrides: {
        root: { backgroundColor: 'transparent' },
      },
    },

    MuiStepIcon: {
      styleOverrides: {
        text: { fontSize: '1.2rem' },
      },
    },

    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontSize: '1.4rem',
          '&.Mui-active': { fontWeight: 600 },
        },
      },
    },

    MuiList: {
      styleOverrides: {
        root: { padding: '0.4rem 0' },
      },
    },

    MuiListItem: {
      styleOverrides: {
        root: { padding: '0.6rem 1.4rem' },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: '32px',
          color: 'rgba(0, 0, 0, 0.5)',
        },
      },
    },

    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '1.4rem',
          color: 'rgba(0, 0, 0, 0.87)',
          fontWeight: 400,
        },
        secondary: {
          fontSize: '1.2rem',
          color: 'rgba(0, 0, 0, 0.5)',
          marginTop: '0.2rem',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          width: '600px',
          maxWidth: '90vw',
          padding: '2.4rem',
          '@media (max-width: 768px)': {
            width: '100vw',
            maxWidth: '100vw',
          },
        },
      },
    },

    MuiBackdrop: {
      styleOverrides: {
        root: {},
      },
    },

    MuiSvgIcon: {
      styleOverrides: {
        root: {},
      },
    },

    MuiGrid: {
      styleOverrides: {
        root: {},
      },
    },
  },
});

/**
 * Create a light or dark Onyx MUI theme.
 * Dark mode toggles CSS [data-theme] for semantic tokens; MUI palette follows.
 */
export function createOnyxTheme(mode = 'light') {
  if (mode !== 'dark') return muiTheme;
  return createTheme(muiTheme, {
    palette: {
      mode: 'dark',
      primary: { main: '#f0f4f5', contrastText: '#000000' },
      secondary: { main: '#cfaefa', contrastText: '#000000' },
      background: { default: '#121414', paper: '#1c1f1f' },
      text: { primary: '#f0f4f5', secondary: '#c3c8c8' },
      divider: '#2a2e2e',
      action: {
        hover: 'rgba(240, 244, 245, 0.06)',
        selected: 'rgba(240, 244, 245, 0.1)',
      },
    },
    typography: {
      allVariants: { color: 'var(--color-text)' },
      h1: { color: 'var(--color-text)' },
      h2: { color: 'var(--color-text)' },
      h3: { color: 'var(--color-text)' },
      h4: { color: 'var(--color-text)' },
      h5: { color: 'var(--color-text)' },
      h6: { color: 'var(--color-text)' },
      body1: { color: 'var(--color-text)' },
      body2: { color: 'var(--color-text-muted)' },
      subtitle1: { color: 'var(--color-text)' },
      subtitle2: { color: 'var(--color-text-muted)' },
      caption: { color: 'var(--color-text-muted)' },
      button: { color: 'var(--color-text)' },
      brandDisplay: { color: 'var(--color-text)' },
      uiBody: { color: 'var(--color-text)' },
      label: { color: 'var(--color-text-muted)' },
      bigFigure: { color: 'var(--color-text)' },
      sectionTitle: { color: 'var(--color-text)' },
    },
    components: {
      MuiDialogActions: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--color-surface)',
            borderTop: '1px solid var(--color-border)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: '#1c1f1f',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            backgroundColor: '#1c1f1f',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: '#2a2e2e',
          },
          head: {
            backgroundColor: '#1c1f1f',
            color: '#c3c8c8',
          },
          stickyHeader: {
            backgroundColor: '#1c1f1f',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--color-surface) !important',
            '&:nth-of-type(even)': {
              backgroundColor: 'var(--color-surface) !important',
            },
            '&:hover': {
              backgroundColor: 'var(--color-bg) !important',
            },
            '&.MuiTableRow-hover:hover': {
              backgroundColor: 'var(--color-bg) !important',
            },
          },
          head: {
            backgroundColor: 'var(--color-surface)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
          outlined: {
            backgroundColor: '#1c1f1f',
          },
        },
      },
    },
  });
}

export default muiTheme;
